import { create } from 'zustand';
import type { ChatMessage } from '@hump/shared-types';
import { docRepository } from '../lib/repository/doc';
import { chatCompletion } from '../lib/ai/provider';
import { buildChatPrompt } from '../lib/ai/prompt';
import { useReaderStore } from './reader';

interface ChatState {
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;

  loadChat: (docId: string) => Promise<void>;
  sendMessage: (docId: string, content: string) => Promise<void>;
  clearChat: (docId: string) => Promise<void>;
}

export const useChatStore = create<ChatState>((set, get) => ({
  messages: [],
  loading: false,
  error: null,

  loadChat: async (docId) => {
    try {
      const messages = await docRepository.getChat(docId);
      set({ messages });
    } catch (e) {
      set({ error: (e as Error).message });
    }
  },

  sendMessage: async (docId, content) => {
    set({ loading: true, error: null });

    const userMsg: ChatMessage = {
      id: 'c_' + crypto.randomUUID(),
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
    };

    const currentMessages = [...get().messages, userMsg];
    set({ messages: currentMessages });

    try {
      const readerState = useReaderStore.getState();
      const allBlocks = readerState.blocks;
      const progress = readerState.docIndex?.meta.progress ?? 0;
      const focusIdx = Math.min(
        Math.max(0, Math.floor(allBlocks.length * progress)),
        Math.max(0, allBlocks.length - 1)
      );
      const start = Math.max(0, focusIdx - 2);
      const end = Math.min(allBlocks.length, focusIdx + 3);
      const contextBlocks = allBlocks.slice(start, end);

      const contextBlockId = allBlocks[focusIdx]?.id;

      const { system, user } = buildChatPrompt(currentMessages, contextBlocks);

      // 5.7 全文问答 RAG（简化版，不走向量检索）：
      // 当用户消息包含「全文/整篇/总结」等关键词，或聚焦窗口为空时，
      // 将当前文档全部 block 的 original 拼接（最多 5000 字符）作为额外上下文
      // 注入到 system prompt，使 AI 能基于整篇内容作答。
      const FULL_DOC_KEYWORDS = ['全文', '整篇', '总结', '全篇', '整体', '概览'];
      const wantsFullDoc =
        FULL_DOC_KEYWORDS.some((kw) => content.includes(kw)) ||
        contextBlocks.length === 0;
      let finalSystem = system;
      if (wantsFullDoc && allBlocks.length > 0) {
        const fullDoc = allBlocks
          .map((b) => b.original)
          .filter(Boolean)
          .join('\n')
          .slice(0, 5000);
        finalSystem = `${system}\n\n---\n以下是整篇文档的全文（最多 5000 字符），供你回答全文类问题时参考：\n${fullDoc}`;
      }

      const response = await chatCompletion([
        { role: 'system', content: finalSystem },
        { role: 'user', content: user },
      ]);

      const assistantMsg: ChatMessage = {
        id: 'c_' + crypto.randomUUID(),
        role: 'assistant',
        content: response,
        contextBlockId,
        createdAt: new Date().toISOString(),
      };

      const finalMessages = [...currentMessages, assistantMsg];
      set({ messages: finalMessages, loading: false });
      await docRepository.saveChat(docId, finalMessages);
    } catch (e) {
      set({ loading: false, error: (e as Error).message });
    }
  },

  clearChat: async (docId) => {
    set({ messages: [], error: null });
    await docRepository.saveChat(docId, []);
  },
}));

// 注册到 window 供 SelectionTranslate 划词追问使用
if (typeof window !== 'undefined') {
  (window as any).__humpChatStore = useChatStore;
}
